import { delay } from './helpers';

export const BLOCKS_MARKER_MIME_TYPE = 'application/x-relume-blocks-marker';
const SAFARI_SUPPORTED_FORMATS: readonly MimeTypes[] = [
  'text/plain',
  'text/html',
];

type MimeTypes =
  | 'text/plain'
  | 'text/html'
  | 'application/json'
  | typeof BLOCKS_MARKER_MIME_TYPE;

export function writeToClipboard(
  formats: Partial<Record<MimeTypes, string>>,
): Promise<void> {
  if (isSafari()) {
    // Safari doesn't support our "copy" event listener method.  It does support
    // navigator.clipboard.write, but that api only supports some mime types.
    const itemFormats: Record<string, string> = {};
    for (const type of SAFARI_SUPPORTED_FORMATS) {
      const v = formats[type];
      if (v) itemFormats[type] = v;
    }
    return navigator.clipboard.write([new ClipboardItem(itemFormats)]);
  }

  // There's an api called "navigator.clipboard.write".  It doesn't work with JSON:
  //   Uncaught (in promise) DOMException: No valid data on clipboard.
  return new Promise<void>((resolve) => {
    const onCopy = (e: ClipboardEvent) => {
      e.preventDefault();
      // Don not let webflow's builtin listener run.  It might show an error
      // that nothing is selected.
      e.stopPropagation();
      e.stopImmediatePropagation();

      for (const [type, data] of Object.entries(formats)) {
        e.clipboardData?.setData(type, data);
      }

      document.removeEventListener('copy', onCopy, {
        capture: true,
      });
      window.removeEventListener('copy', onCopy as (e: Event) => void);
      resolve();
    };
    document.addEventListener('copy', onCopy, { capture: true });
    // 1. If focus is inside the website iframe, it `document::copy` won't fire.
    //    So we listen to the window too.
    // 2. Typescript doesn't know about `window::copy`, hence the cast.
    window.addEventListener('copy', onCopy as (e: Event) => void);
    // We don't use navigator.clipboard because it'd throw:
    //   DOMException: Type application/json not supported on write
    document.execCommand('copy');
  }).then(() => {
    // If we don't wait a sec, sometimes webflow sees a empty paste and displays
    // the error message: "The clipboard is empty"
    return delay(25);
  });
}

export function isSafari() {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}
